import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface IndustryContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', IndustryContent> = {
  en: {
    metaTitle: 'Identity & access for retail',
    metaDescription:
      'Store-friendly POS authentication, automated seasonal lifecycle, PCI-DSS-ready PAM, and omnichannel customer identity for retailers and franchises.',
    heroEyebrow: 'Industries · Retail',
    heroTitle: ['From the POS', 'to the warehouse.'],
    heroBody:
      'Fast store sign-in, automated seasonal hiring, brokered payment-system access, and PCI-DSS-ready audit: one identity platform from POS to back office.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'PCI-DSS evidence ready',
      'Shared-device POS sign-in',
      "Trusted by McDonald's, Godiva",
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What retailers get with Monofor.',
    pillarsBody:
      'Retail identity is high-volume, high-churn, and high-stakes for the payment-card environment. Monofor handles all three on the same platform.',
    pillars: [
      {
        title: 'Store-friendly authentication',
        description:
          'POS terminals, kiosks, back-office stations: Monosign signs in associates fast, supports shared-device profiles, and survives flaky store connectivity.',
        bullets: [
          'Shared-device sign-in for POS and kiosks',
          'PIN, badge, and biometric flows',
          'Offline OTP for disconnected stores',
          'Rapid associate role-switching at the till',
        ],
      },
      {
        title: 'High-turnover lifecycle, automated',
        description:
          'Retail has seasonal hiring, transfers, and rapid offboarding. Monosign automates joiner / mover / leaver so access matches reality every day.',
        bullets: [
          'HRIS-driven joiner / mover / leaver',
          'Seasonal worker bundles with hard expiry',
          'Store-transfer access updates within minutes',
          'Same-day offboarding with audit trail',
        ],
      },
      {
        title: 'PCI-DSS without theatre',
        description:
          'PAM for payment systems, vault for card-data-environment credentials, audit reports that match the assessor checklist exactly.',
        bullets: [
          'Brokered access to payment systems',
          'CDE credential vaulting and rotation',
          'PCI-DSS pre-built report templates',
          'Per-store evidence isolation',
        ],
      },
      {
        title: 'Customer identity for omnichannel',
        description:
          'Shoppers move between web, mobile, and store. Monosign keeps the identity coherent, without storing more data than you need.',
        bullets: [
          'B2C identity with social and email flows',
          'Loyalty-program identity continuity',
          'Consent and preference center',
          'Minimal-data-collection design',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Sign in. Automate. Lock down.',
    steps: [
      {
        title: 'Sign in associates fast',
        description:
          'Deploy PIN, badge, and offline OTP flows on POS and back-office workstations. Shared-device profiles keep the line moving.',
      },
      {
        title: 'Automate the churn',
        description:
          'Drive joiner / mover / leaver from your HRIS. Seasonal hires get bundles with expiry; offboarding is same-day.',
      },
      {
        title: 'Lock down the payment surface',
        description:
          'Vault CDE credentials, broker every privileged session, and export the PCI-DSS reports your assessor expects.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'User lifecycle',
        description: 'Joiner / mover / leaver automation across HRIS and downstream apps.',
        href: '/sign/lifecycle',
      },
      {
        label: 'Privileged vault',
        description: 'CDE credential vaulting and rotation.',
        href: '/pam/vault',
      },
      {
        label: 'Audit & compliance',
        description: 'Use case: PCI-DSS evidence as a continuous practice.',
        href: '/solutions/audit-compliance',
      },
    ],
  },
  tr: {
    metaTitle: 'Perakende için kimlik ve erişim',
    metaDescription:
      'Perakendeciler ve franchise’lar için mağaza dostu POS kimlik doğrulaması, otomatik sezonluk yaşam döngüsü, PCI-DSS’e hazır PAM ve çok kanallı müşteri kimliği.',
    heroEyebrow: 'Sektörler · Perakende',
    heroTitle: ['POS’tan', 'depoya kadar.'],
    heroBody:
      'Hızlı mağaza girişi, otomatik sezonluk işe alım, aracılı ödeme sistemi erişimi ve PCI-DSS’e hazır denetim: POS’tan arka ofise tek kimlik platformu.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'PCI-DSS kanıtına hazır',
      'Ortak cihazda POS girişi',
      "McDonald's ve Godiva’nın tercihi",
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Perakendecilerin Monofor ile elde ettikleri.',
    pillarsBody:
      'Perakende kimliği yüksek hacimlidir, sirkülasyonu yüksektir ve ödeme kartı ortamı için riski büyüktür. Monofor üçünü de aynı platformda karşılar.',
    pillars: [
      {
        title: 'Mağaza dostu kimlik doğrulama',
        description:
          'POS terminalleri, kiosklar, arka ofis istasyonları: Monosign çalışanları hızla oturum açtırır, ortak cihaz profillerini destekler ve zayıf mağaza bağlantısında bile çalışır.',
        bullets: [
          'POS ve kiosklar için ortak cihaz girişi',
          'PIN, kart ve biyometrik akışlar',
          'Bağlantısız mağazalar için çevrimdışı OTP',
          'Kasada hızlı çalışan rol değiştirme',
        ],
      },
      {
        title: 'Yüksek sirkülasyonlu yaşam döngüsü, otomatik',
        description:
          'Perakendede sezonluk işe alım, transferler ve hızlı işten çıkış vardır. Monosign işe giriş / geçiş / ayrılış süreçlerini otomatikleştirir; erişim her gün gerçeğe uyar.',
        bullets: [
          'İK sistemi (HRIS) kaynaklı işe giriş / geçiş / ayrılış',
          'Kesin süre sonlu sezonluk çalışan paketleri',
          'Dakikalar içinde mağaza transferi erişim güncellemeleri',
          'Denetim iziyle aynı gün işten çıkış işlemleri',
        ],
      },
      {
        title: 'Gösteriş değil, PCI-DSS',
        description:
          'Ödeme sistemleri için PAM, kart verisi ortamı (CDE) kimlik bilgileri için kasa, değerlendirici kontrol listesiyle birebir eşleşen denetim raporları.',
        bullets: [
          'Ödeme sistemlerine aracılı erişim',
          'CDE kimlik bilgisi kasası ve rotasyonu',
          'Hazır PCI-DSS rapor şablonları',
          'Mağaza başına kanıt izolasyonu',
        ],
      },
      {
        title: 'Çok kanallı perakende için müşteri kimliği',
        description:
          'Müşteriler web, mobil ve mağaza arasında hareket eder. Monosign kimliği tutarlı tutar, ihtiyacınızdan fazla veri saklamadan.',
        bullets: [
          'Sosyal medya ve e-posta akışlarıyla B2C kimlik',
          'Sadakat programında kimlik sürekliliği',
          'Rıza ve tercih merkezi',
          'Asgari veri toplama tasarımı',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Oturum açın. Otomatikleştirin. Güvenceye alın.',
    steps: [
      {
        title: 'Çalışanları hızla oturum açtırın',
        description:
          'POS ve arka ofis iş istasyonlarında PIN, kart ve çevrimdışı OTP akışlarını devreye alın. Ortak cihaz profilleri kuyruğu bekletmez.',
      },
      {
        title: 'Sirkülasyonu otomatikleştirin',
        description:
          'İşe giriş / geçiş / ayrılışı İK sisteminizden (HRIS) yönetin. Sezonluk çalışanlar süre sonlu paketler alır; işten çıkış aynı gün tamamlanır.',
      },
      {
        title: 'Ödeme yüzeyini güvenceye alın',
        description:
          'CDE kimlik bilgilerini kasaya alın, her ayrıcalıklı oturumu aracılayın ve değerlendiricinizin beklediği PCI-DSS raporlarını dışa aktarın.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Kullanıcı yaşam döngüsü',
        description: 'HRIS ve alt uygulamalar genelinde işe giriş / geçiş / ayrılış otomasyonu.',
        href: '/sign/lifecycle',
      },
      {
        label: 'Ayrıcalıklı kasa',
        description: 'CDE kimlik bilgisi kasası ve rotasyonu.',
        href: '/pam/vault',
      },
      {
        label: 'Denetim ve uyum',
        description: 'Kullanım senaryosu: sürekli bir pratik olarak PCI-DSS kanıtı.',
        href: '/solutions/audit-compliance',
      },
    ],
  },
}

type Props = { locale: string }

export default function RetailPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/industries/retail"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
